/**
 * #2109 R2 (lane L6): the poll card's language helpers. Pure, kept apart from
 * the components so each component file exports only components
 * (react-refresh).
 */
import type { MintedI18n, PollQuestion } from '@/types/polls';
import { isTranslationLanguage } from '@/lib/formI18n';
import type { FormLanguage } from '@/lib/formDefinitions';

// A question minted from a translated form version carries `prompt_i18n`,
// `section_label_i18n` and `choices[].label_i18n` — the CURRENT translations
// of the languages that version offered, frozen at mint (backend
// services/pollI18n.js). A stale translation never travelled, so whatever is
// here may be shown. A code with no entry renders English.

function pickI18n(map: MintedI18n | null | undefined, locale: string): string | null {
  if (!isTranslationLanguage(locale) || !map) return null;
  const text = map[locale];
  return typeof text === 'string' && text.trim() !== '' ? text : null;
}

/**
 * The question as the reader sees it: each string replaced where the locale
 * carries a translation, English everywhere else, and every `*_i18n` map
 * removed so nothing downstream reads an unchosen language. Pure and
 * idempotent; the input is never mutated. Choice ids and values never change,
 * so a vote always posts the same ids whatever the language.
 */
export function localizeQuestion(q: PollQuestion, locale: string): PollQuestion {
  const { prompt_i18n, section_label_i18n, ...rest } = q;
  return {
    ...rest,
    prompt: pickI18n(prompt_i18n, locale) ?? q.prompt,
    section_label: q.section_label == null
      ? q.section_label
      : pickI18n(section_label_i18n, locale) ?? q.section_label,
    choices: q.choices
      ? q.choices.map(({ label_i18n, ...c }) => ({ ...c, label: pickI18n(label_i18n, locale) ?? c.label }))
      : q.choices,
  };
}

/** Whether any string of `q` has a translation in `locale`. */
function carries(q: PollQuestion, locale: string): boolean {
  return pickI18n(q.prompt_i18n, locale) !== null
    || pickI18n(q.section_label_i18n, locale) !== null
    || (q.choices ?? []).some((c) => pickI18n(c.label_i18n, locale) !== null);
}

/**
 * The key of one displayed string of a poll, as `staleFallbacks` lists it.
 * Question and choice ids are the poll's own, so a key is unique per card.
 */
export const pollStringKey = {
  prompt: (questionId: string) => `${questionId}.prompt`,
  section: (questionId: string) => `${questionId}.section`,
  choice: (questionId: string, choiceId: string) => `${questionId}.choice.${choiceId}`,
};

/**
 * #2133 item 4: the strings of a translated poll that are still in English.
 *
 * A stale translation is dropped at mint, so the poll cannot tell a stale
 * string from one that was never translated. It does not need to: in both
 * cases the family reads English on a card that is otherwise in their
 * language, and the per-string note says exactly that, "shown in English".
 * Only asked when the card carries the reader's language at all; a card with
 * none says so once, in the notice (R2-B17), not on every string.
 */
function englishFallbacks(questions: PollQuestion[], locale: string): string[] {
  const keys: string[] = [];
  for (const q of questions) {
    if (pickI18n(q.prompt_i18n, locale) === null) keys.push(pollStringKey.prompt(q._id));
    if (q.section_label?.trim() && pickI18n(q.section_label_i18n, locale) === null) {
      keys.push(pollStringKey.section(q._id));
    }
    for (const c of q.choices ?? []) {
      if (pickI18n(c.label_i18n, locale) === null) keys.push(pollStringKey.choice(q._id, c._id));
    }
  }
  return keys;
}

/**
 * The card's language lines, in the shape of the form renderer's
 * `FormLocalization` so the same notice component renders both:
 * - `machine` (R2-B18): at least one displayed string is a translation. Every
 *   R2 language ships as machine translation (B14), so no status is needed.
 * - `notOffered` (R2-B1, R2-B17): a non-English reader and no string in their
 *   language — a quick send, or a minted poll whose version did not offer it.
 * - `staleFallbacks` (#2133): on a card in the reader's language, the keys
 *   (`pollStringKey`) of the strings still shown in English.
 */
export function pollLanguageState(questions: PollQuestion[], requested: string): {
  requested: string;
  locale: FormLanguage;
  notOffered: boolean;
  machine: boolean;
  staleFallbacks: string[];
} {
  const machine = isTranslationLanguage(requested) && questions.some((q) => carries(q, requested));
  return {
    requested,
    locale: machine && isTranslationLanguage(requested) ? requested : 'en',
    notOffered: requested !== 'en' && !machine,
    machine,
    staleFallbacks: machine ? englishFallbacks(questions, requested) : [],
  };
}
